import React from "react";
import { connect, type ConnectedProps } from "react-redux";
import { map, orderBy, some, isEmpty, get, size } from "lodash";
import { Card } from "react-bootstrap";
import { paymentStatusToEnglish } from "statics/statusToEnglishMapping";
import { FiList } from "react-icons/fi";

import moment from "moment";
import IconTable from "components/IconTable";
import type { IconTableColumn } from "components/IconTable";
import IconMessageCard from "components/IconMessageCard";
import Button from "components/Button";
import ConfirmAchCancellationModal from "../dashboard/ConfirmAchCancellationModal";
import IconTableHeader from "components/IconTableHeader";
import type { RootState } from "types/state";

import "./Transactions.scss";

const achDepositCategory = "IRA_CONTRIBUTION_ADHOC";

const mapStateToProps = (state: RootState) => {
  const descendingTransactions = orderBy(
    state.transactions.transactions,
    "createdAt",
    "desc"
  );
  return {
    transactions: descendingTransactions,
  };
};

const connector = connect(mapStateToProps, null);

type Props = ConnectedProps<typeof connector>;

interface State {
  showConfirmationModal: boolean;
  pendingCancellationId: string | null;
}

interface TransactionRow {
  createdAt: string;
  status: string | null | undefined;
  taxYear: number | null | undefined;
  pendingActionACH: React.ReactNode;
  memo: string | null | undefined;
}

export class TransactionsTable extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      showConfirmationModal: false,
      pendingCancellationId: null,
    };
  }
  _getTransactionData = (containsPendingAch: boolean): TransactionRow[] => {
    return map(this.props.transactions, (transaction) => {
      const createdAt = moment(transaction.createdAt).format("MMM Do YYYY");
      let action = null;
      const cancellationPending =
        transaction.cancellationStatus === "SUBMITTED";
      if (
        containsPendingAch &&
        transaction.status === "PENDING" &&
        !cancellationPending &&
        transaction.subCategory === achDepositCategory
      ) {
        action = (
          <>
            <Button
              btnLabel="cancel"
              name="cancel"
              color="red"
              type="button"
              onClick={() => this._openConfirmModal(transaction.id)}
            />
          </>
        );
      }
      let status =
        (paymentStatusToEnglish as Record<string, string>)[
          transaction.status as string
        ] || transaction.status;
      status = cancellationPending ? "Cancellation pending" : status;

      const taxYear = get(transaction, "taxYear");

      const pendingActionACH = containsPendingAch ? action : null;

      return {
        createdAt,
        status,
        taxYear,
        pendingActionACH,
        memo: transaction.memo,
      };
    });
  };

  _buildTransactionColumns = (): Array<IconTableColumn<TransactionRow>> => {
    const containsPendingAch = this._transactionsContainPendingAchDeposit();
    const columns: Array<IconTableColumn<TransactionRow>> = [
      {
        label: "Transaction Date",
        key: "createdAt",
      },

      {
        label: "Description",
        key: "memo",
      },
      {
        label: "Status",
        key: "status",
      },
      {
        label: "Tax Year",
        key: "taxYear",
      },
      {
        customComponent: (props: TransactionRow) => {
          return (
            <>
              {containsPendingAch && (
                <span style={{ width: 125 }}>{props.pendingActionACH}</span>
              )}
            </>
          );
        },
      },
    ];
    return columns;
  };

  _openConfirmModal = (pendingCancellationId: string) => {
    this.setState({
      pendingCancellationId,
      showConfirmationModal: true,
    });
  };

  _closeConfirmModal = () => {
    this.setState({
      showConfirmationModal: false,
      pendingCancellationId: null,
    });
  };

  _transactionsContainPendingAchDeposit = () => {
    return some(
      this.props.transactions,
      (t) =>
        t.subCategory === achDepositCategory &&
        t.status === "PENDING" &&
        t.cancellationStatus !== "SUBMITTED"
    );
  };

  _blankSlateBody() {
    return (
      <IconMessageCard
        header="There are no transactions at this time."
        subheader="This usually means you have
          not made a contribution yet through your employer or bank account."
        icon={<FiList color="white" stroke="#60A4BF" size={16} />}
      />
    );
  }

  render() {
    const containsPendingAch = this._transactionsContainPendingAchDeposit();

    const columns = this._buildTransactionColumns();
    const data = this._getTransactionData(containsPendingAch);

    let body;

    if (isEmpty(data)) {
      body = this._blankSlateBody();
    } else {
      body = (
        <>
          {this.state.showConfirmationModal && (
            <ConfirmAchCancellationModal
              onClose={this._closeConfirmModal}
              transactionId={this.state.pendingCancellationId as string}
            />
          )}
          <IconTableHeader
            tableHeader="Recent Transactions"
            tableCount={size(this.props.transactions)}
          />
          <IconTable columns={columns} data={data} />
        </>
      );
    }

    return <Card className="transaction-table">{body}</Card>;
  }
}

export default connector(TransactionsTable);
